import {
    errorMessageTypes,
    similarityMessageTypes,
    similarityUpdate,
    photoApproved,
    photoRejected,
    requestWentWrong,
    photoVerificationType,
} from '../../actions/IdVerificationActions';

import ApiWithSessionService from './ApiWithSessionService';
import stateCache from '../StateCache';
import storeManager from '../../store/StoreManager';
import BlobToBase64Service from './BlobToBase64Service';
import apiService from '../api/ApiService';
import sessionReader from '../../store/readHelpers/SessionReader';
import similarityChecker from './SimilarityChecker';
import idVerificationService from './IdVerificationService';

/**
 * This "rejectIfUnhandled" function and the "handledError" variable allow
 * catching errors concerning a request, without modifying the original Promise.
 *
 * There are several constraints at play here:
 * - If the query, we must stop the chain of promises.
 *
 * - If the request is unsuccessful, an error message should be displayed but
 *   the promise should not be left as an unhandled rejected promise.
 *
 * - If the request was successful but there is another error during the
 *   analysis of the response, the promise must be rejected but left as
 *   unhandled so that there is an error in the JavaScript development console.
 *
 * Indeed, the ".catch" function creates a new Promise and by default this
 * Promise succeeds with an empty value. Or, in our case, it is desirable to
 * stop the chain of promises, as there is no point in trying to parse a request
 * if it failed. To be fair, the request error handling feature isn't designed
 * for that anyway.
 *
 * So to stop the chain of Promises, parseRequestError returns a rejected
 * Promise.
 *
 * However, if we do not add a .catch at the very end of the chain, the rejected
 * Promise is considered unhandled and therefore triggers an error in the
 * console. This is undesirable if an error message is already going to be
 * displayed on the screen.
 *
 * The solution is to use variable “handledError”. When parseRequestError
 * handled an error, it returns a rejected promise with “handledError”. So at
 * the end, if the rejectIfUnhandled function catches the handledError value, it
 * ignores the error. Otherwise, it rejects the error again to display in the
 * console.
 *
 * At the end, the way to go is to catch with parseRequestError early in the
 * chain, just after the request, and to add another catch at the whole end of
 * the chain with rejectIfUnhandled.
 */
const handledError = new Error('');

// I think the e argument will be used in the future. I added an ignore rule.
// eslint-disable-next-line no-unused-vars
function parseRequestError() {
    storeManager.dispatch(requestWentWrong());
    return Promise.reject(handledError);
}


function rejectIfUnhandled(e) {
    if (e !== handledError) {
        return Promise.reject(e);
    }

    return null;
}

function checkSimilarity() {
    /**
     * Put catch before then to not catch errors triggered callback.
     * Errors in callback is the result of a problem in the code and not in the
     * network.
     */
    return ApiWithSessionService.fetch(
        {
            type: photoVerificationType.SIMILARITY,
        },
    )
        .then(
            fetchToJson,
        )
        .catch(parseRequestError)
        .then((result) => {
            if (similarityChecker.shouldPassSimilarityCheck(result)) {
                const stateCacheInstance = stateCache.getStateCacheInstance();
                stateCacheInstance.setInitialIdVerificationCompletion(true);

                storeManager.dispatch(similarityUpdate(similarityMessageTypes.APPROVED));

                if (sessionReader.isPhotoUploadUponIdVerificationResolutionEnabled()) {
                    return true;
                }

                const assessmentRunId = sessionReader.getAssessmentRunId(true);
                return apiService.uploadAutomaticIdVerificationPhotos(assessmentRunId);
            }
            storeManager.dispatch(similarityUpdate(similarityMessageTypes.REJECTED));
            return true;
        })
        .catch(rejectIfUnhandled);
}

function parseUploadPhotoResponseError(response) {
    const { Success: success } = response;

    if (success) {
        return errorMessageTypes.none;
    }

    const { FailureCause: failureCause } = response;
    const errorMessage = errorMessageTypes[failureCause];

    if (errorMessage != null) {
        return errorMessage;
    }

    throw new Error('ID photo was rejected for a unknown reason.');
}

function fetchToJson(response) {
    return response.json();
}

function uploadPhoto(photo) {
    const { idverification } = storeManager.getState();
    const isIdCard = idVerificationService.isIdCardStep(idverification);
    const type = isIdCard ? photoVerificationType.ID_CARD : photoVerificationType.PHOTO;

    return BlobToBase64Service.encode(photo)
        .then((base64image) => {
            return ApiWithSessionService.fetch(
                {
                    type,
                    base64image,
                    allowOverwrite: idVerificationService.isRequestPhotoRetakeVerificationStatus(idverification),
                },
            );
        })
        .then(
            fetchToJson,
        )
        .catch(parseRequestError)
        .then((result) => {
            const errorMessage = parseUploadPhotoResponseError(result);

            if (errorMessage === errorMessageTypes.none) {
                storeManager.dispatch(photoApproved());
                return;
            }

            storeManager.dispatch(photoRejected(errorMessage));
        })
        .catch(rejectIfUnhandled);
}

export {
    checkSimilarity,
    parseUploadPhotoResponseError,
    uploadPhoto,
};
